import { fetchApi } from '@/lib/api';
import type {
  InboxCatalogHit,
  InboxChannelSummary,
  InboxChannelSwitches,
  InboxConversationDetail,
  InboxConversationContext,
  InboxConversationSummary,
  InboxCounts,
  InboxEvent,
  InboxGoogleProjectInput,
  InboxLaneInput,
  InboxListResponse,
  InboxMember,
  InboxNeedsReplyRow,
  InboxForwardInput,
  InboxNewEmailInput,
  InboxConversationOrders,
  InboxMessageTranslation,
  InboxSignatureDetails,
  InboxSignaturesResponse,
  InboxProjectCheck,
  InboxReplyFile,
  InboxReplyInput,
  InboxSavedReply,
  InboxSavedReplyInput,
  InboxSuggestedReply,
  InboxSort,
  InboxSenderRule,
  InboxSenderRuleInput,
  InboxStatus,
  InboxTab,
} from '@shared/types/inbox';
import type { InboxReplyPresence } from '@shared/types/tasks';
import type { InboxAwayInput } from '@shared/inbox-away';

/** Shared inbox API (docs/shared-inbox-plan.md). */

const base = '/inbox';

function json(method: string, body?: unknown): RequestInit {
  return { method, body: body === undefined ? undefined : JSON.stringify(body) };
}

export interface InboxChannelResponse {
  channel: InboxChannelSummary | null;
  members: InboxMember[];
  key_configured: boolean;
}

export function getInboxChannel() {
  return fetchApi<InboxChannelResponse>(`${base}/channel`);
}

export function checkGoogleProject(input: InboxGoogleProjectInput) {
  return fetchApi<{ checks: InboxProjectCheck[]; ok: boolean }>(`${base}/channel/google-project/check`, json('POST', input));
}

export function saveGoogleProject(input: InboxGoogleProjectInput) {
  return fetchApi<{ channel: InboxChannelSummary }>(`${base}/channel/google-project`, json('PUT', input));
}

export function startGmailConnect() {
  return fetchApi<{ url: string }>(`${base}/connect/start`);
}

export function finishGmailConnect(code: string, state: string) {
  return fetchApi<{ channel: InboxChannelSummary; watch_error: string | null }>(`${base}/connect/callback`, json('POST', { code, state }));
}

export function syncInboxNow() {
  return fetchApi<{ ok: boolean }>(`${base}/channel/sync`, json('POST', {}));
}

export function setInboxSwitches(patch: Partial<InboxChannelSwitches>) {
  return fetchApi<{ channel: InboxChannelSummary }>(`${base}/channel/switches`, json('PATCH', patch));
}

export function importMoreHistory() {
  return fetchApi<{ since: string }>(`${base}/channel/import-more`, json('POST', {}));
}

/** Pause: jflo stops syncing and writing to Gmail. Resume catches up. */
export function pauseInbox(paused: boolean) {
  return fetchApi<{ channel: InboxChannelSummary }>(`${base}/channel/pause`, json('POST', { paused }));
}

/** Save one-line topic label descriptions (by label id); an empty text removes one. */
/** Email signatures and the team template: everyone's for an admin, otherwise your own (`only: 'me'` for just yours). */
/** @ in a reply: the website's collections, products and variants matching `q`. */
export function searchInboxCatalogLinks(q: string) {
  return fetchApi<{ results: InboxCatalogHit[] }>(`${base}/catalog-links?q=${encodeURIComponent(q)}`);
}

export function getInboxSignatures(only?: 'me') {
  return fetchApi<InboxSignaturesResponse>(`${base}/signatures${only ? '?only=me' : ''}`);
}

/** Save someone's details for the template and/or their own HTML ("" goes back to the template). `warnings`: pictures that couldn't be copied. */
export function saveInboxSignature(userId: string, input: { html?: string; details?: InboxSignatureDetails }) {
  return fetchApi<{ warnings: string[] }>(`${base}/signatures/${userId}`, json('PUT', input));
}

/** Save the team's signature template and its {logo} (a link, pasted data, or the saved `jflo-sig:` src); no HTML and no logo removes it. */
export function saveInboxSignatureTemplate(input: { html: string; logo: string | null; logo_width: number | null }) {
  return fetchApi<{ warnings: string[] }>(`${base}/signature-template`, json('PUT', input));
}

/** The business name in the From line; "" goes back to the team's name without "(Pty) Ltd". */
export function saveInboxSenderName(name: string) {
  return fetchApi<{ channel: InboxChannelSummary }>(`${base}/channel/sender-name`, json('PUT', { name }));
}

export function saveLabelDescriptions(descriptions: Record<string, string>) {
  return fetchApi<{ channel: InboxChannelSummary }>(`${base}/channel/label-descriptions`, json('PUT', { descriptions }));
}

export function disconnectInbox() {
  return fetchApi<{ channel: InboxChannelSummary }>(`${base}/channel/disconnect`, json('POST', {}));
}

export function setMemberHandle(userId: string, handle: string) {
  return fetchApi<{ handle: string; members: InboxMember[] }>(`${base}/members/${userId}/handle`, json('PUT', { handle }));
}

/** Out of office (T-74): set a person's away period and cover (their own, or anyone's for an admin). */
export function setInboxAway(userId: string, input: InboxAwayInput) {
  return fetchApi<{ members: InboxMember[] }>(`${base}/away/${userId}`, json('PUT', input));
}

/** Out of office (T-74): they're back. Covered conversations still open go back to them. */
export function clearInboxAway(userId: string) {
  return fetchApi<{ members: InboxMember[] }>(`${base}/away/${userId}`, { method: 'DELETE' });
}

export function getInboxCounts() {
  return fetchApi<{ counts: InboxCounts | null }>(`${base}/counts`);
}

/** `assignee`: a person's id, or "none" for unassigned. */
export function listInboxConversations(opts: {
  tab: InboxTab;
  q?: string;
  label?: string | null;
  assignee?: string | null;
  cursor?: string | null;
  /** newest or oldest last email first, or 'priority': the AI's priority first (Jev's triage), then newest. */
  sort?: InboxSort;
}) {
  const params = new URLSearchParams({ tab: opts.tab });
  if (opts.sort && opts.sort !== 'newest') params.set('sort', opts.sort);
  if (opts.q) params.set('q', opts.q);
  if (opts.label) params.set('label', opts.label);
  if (opts.assignee) params.set('assignee', opts.assignee);
  if (opts.cursor) params.set('cursor', opts.cursor);
  return fetchApi<InboxListResponse>(`${base}/conversations?${params.toString()}`);
}

/**
 * `open`: this is the person opening it, so mark it read (in Gmail too, when the switch is on).
 * `seen`: it's on their screen, so the notes mentioning them are seen (T-392).
 */
export function getInboxConversation(id: string, opts: { open?: boolean; seen?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.open) params.set('open', '1');
  if (opts.seen) params.set('seen', '1');
  const search = params.toString();
  return fetchApi<{ conversation: InboxConversationDetail; mentions_seen?: boolean }>(`${base}/conversations/${id}${search ? `?${search}` : ''}`);
}

export function assignConversation(id: string, userId: string | null) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/assign`, json('POST', { user_id: userId }));
}

export function setConversationStatus(id: string, status: InboxStatus) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/status`, json('POST', { status }));
}

/** Snooze until an ISO time, or wake it now with null. */
export function snoozeConversation(id: string, until: string | null) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/snooze`, json('POST', { until }));
}

export function setConversationUnread(id: string, unread: boolean) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/unread`, json('POST', { unread }));
}

export function moveConversationLane(id: string, input: InboxLaneInput) {
  return fetchApi<{ moved: number; rule_match: string | null }>(`${base}/conversations/${id}/lane`, json('POST', input));
}

export function markConversationSpam(id: string, always: boolean) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/spam`, json('POST', { always }));
}

export function setConversationLabel(id: string, labelId: string, on: boolean) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/labels`, json('POST', { label_id: labelId, on }));
}

/**
 * An internal note (never sent to the customer); `text` may hold `@[Name](user:id)` mentions.
 * With `replyTo`, a reply in that note's thread (T-84).
 */
export function addInboxNote(id: string, text: string, replyTo?: string) {
  return fetchApi<{ note: InboxEvent }>(`${base}/conversations/${id}/notes`, json('POST', { text, reply_to: replyTo ?? null }));
}

/** Change your note or reply (for INBOX_NOTE_EDIT_MINUTES after posting it, while it's the latest in its thread). */
export function editInboxNote(id: string, noteId: string, text: string) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/notes/${noteId}`, json('PATCH', { text }));
}

export function deleteInboxNote(id: string, noteId: string) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/notes/${noteId}`, json('DELETE'));
}

/** Queue a reply from hello@; it sends after the undo window unless cancelled. */
export function sendInboxReply(id: string, input: InboxReplyInput) {
  return fetchApi<{ outbox_id: string; send_at: string }>(`${base}/conversations/${id}/reply`, json('POST', input));
}

/** Queue a forward of one email (T-76); like a reply, it sends after the undo window unless cancelled. */
export function sendInboxForward(id: string, input: InboxForwardInput) {
  return fetchApi<{ outbox_id: string; send_at: string }>(`${base}/conversations/${id}/forward`, json('POST', input));
}

/** A new email from the shared address; sent at once. Files for it are uploaded under `new`. */
export function sendNewEmail(input: InboxNewEmailInput) {
  return fetchApi<{ conversation_id: string | null }>(`${base}/new-email`, json('POST', input));
}

/** Upload a file for a reply being written; its id goes in the reply's `attachments`. */
export function uploadReplyFile(id: string, file: File) {
  const form = new FormData();
  form.append('file', file, file.name || 'pasted-image.png');
  return fetchApi<{ file: InboxReplyFile }>(`${base}/conversations/${id}/reply-files`, { method: 'POST', body: form });
}

/** Fresh signed links for a draft's uploads (pictures in a reopened draft). */
export function replyFileLinks(id: string, ids: string[]) {
  return fetchApi<{ links: Record<string, string> }>(`${base}/conversations/${id}/reply-files/links`, json('POST', { ids }));
}

export function deleteReplyFile(id: string, fileId: string) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/reply-files/${fileId}`, { method: 'DELETE' });
}

/** Undo a queued reply; `cancelled` is false when it had already gone. */
export function cancelInboxReply(id: string, outboxId: string) {
  return fetchApi<{ cancelled: boolean }>(`${base}/conversations/${id}/reply/${outboxId}/cancel`, json('POST', {}));
}

/** Tell the team you have this Conversation open (every 20 seconds while it is). */
export function pingInboxViewing(id: string) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/viewing`, json('POST', {}));
}

/** Tell the team someone is writing a reply here (throttled by the caller). */
/** Tell the team where your reply stands: typing, the box open with a draft, or closed. */
export function pingInboxTyping(id: string, state: InboxReplyPresence = 'typing') {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/typing`, json('POST', { state }));
}

/** Summarise a Conversation with AI now (it normally happens after each sync). */
export function summariseInboxConversation(id: string) {
  return fetchApi<{ summary: InboxConversationSummary }>(`${base}/conversations/${id}/summary`, json('POST', {}));
}

/** Suggest a reply with AI now ("Try again", or "Suggest a reply" in the reply box, maybe with a direction like "offer a replacement"). */
export function suggestInboxReply(id: string, direction = '') {
  return fetchApi<{ reply: InboxSuggestedReply }>(`${base}/conversations/${id}/suggested-reply`, json('POST', direction.trim() ? { direction } : {}));
}

/** `withUse`: with how often each was used, and picked by Jev, in the last 30 days (the settings card). */
export function listSavedReplies(withUse = false) {
  return fetchApi<{ replies: InboxSavedReply[] }>(`${base}/saved-replies${withUse ? '?use=1' : ''}`);
}

/** A saved reply, or a suggested reply built on some, went into a reply (for the 30-day counts). Fire and forget. */
export function recordSavedReplyUse(conversationId: string, use: { saved_reply_id: string } | { suggested: true; saved_reply_ids: string[] }) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${conversationId}/saved-reply-used`, json('POST', use));
}

export function createSavedReply(input: InboxSavedReplyInput) {
  return fetchApi<{ replies: InboxSavedReply[] }>(`${base}/saved-replies`, json('POST', input));
}

export function updateSavedReply(id: string, input: InboxSavedReplyInput) {
  return fetchApi<{ replies: InboxSavedReply[] }>(`${base}/saved-replies/${id}`, json('PATCH', input));
}

export function deleteSavedReply(id: string) {
  return fetchApi<{ replies: InboxSavedReply[] }>(`${base}/saved-replies/${id}`, json('DELETE'));
}

/** Who handles each department (Jev's department id → user id), for "Assign to …?". */
export function saveDepartmentOwners(owners: Record<string, string | null>) {
  return fetchApi<{ channel: InboxChannelSummary }>(`${base}/channel/department-owners`, json('PUT', { owners }));
}

/** Translate an email into English with AI; kept with the email, so asking again is free. */
export function translateInboxMessage(conversationId: string, messageId: string) {
  return fetchApi<{ translation: InboxMessageTranslation }>(`${base}/conversations/${conversationId}/messages/${messageId}/translate`, json('POST', {}));
}

/** The side panel: the Contact's other Conversations and their supplier. */
export function getConversationContext(id: string) {
  return fetchApi<{ context: InboxConversationContext }>(`${base}/conversations/${id}/context`);
}

/** The Conversation's Shopify orders (live), and the customer's other orders to link. */
export function getConversationOrders(id: string) {
  return fetchApi<InboxConversationOrders>(`${base}/conversations/${id}/orders`);
}

/** "Link an order" by its number; returns the orders as they are now. */
export function linkConversationOrder(id: string, order: string) {
  return fetchApi<InboxConversationOrders>(`${base}/conversations/${id}/orders`, json('POST', { order }));
}

/** "Not this conversation". */
export function unlinkConversationOrder(id: string, order: string) {
  return fetchApi<InboxConversationOrders>(`${base}/conversations/${id}/orders/unlink`, json('POST', { order }));
}

/** What a Conversation was before a bulk change, so it can be undone. */
export interface InboxSnapshot {
  id: string;
  status: InboxStatus;
  lane: 'inbox' | 'other';
}

export function bulkInbox(ids: string[], action: 'done' | 'other' | 'inbox') {
  return fetchApi<{ count: number; previous: InboxSnapshot[] }>(`${base}/bulk`, json('POST', { ids, action }));
}

/** Undo a bulk change: each goes back to the Status and lane it had. */
export function restoreInbox(items: InboxSnapshot[]) {
  return fetchApi<{ count: number }>(`${base}/bulk`, json('POST', { action: 'restore', items }));
}

/** Done conversations the AI says still owe a reply or a promised action. */
export function listNeedsReply() {
  return fetchApi<{ rows: InboxNeedsReplyRow[] }>(`${base}/needs-reply`);
}

/** "It's fine": no reply needed (until the next email). */
export function dismissNeedsReply(id: string) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/needs-reply/dismiss`, json('POST', {}));
}

/**
 * ✕ on an AI suggestion, until the next email: not this Status (by value), not this label (by name),
 * no website task, not this suggested reply, or not this assignment (by user id).
 */
export function dismissAiSuggestion(id: string, kind: 'status' | 'label' | 'task' | 'reply' | 'assign', value: string) {
  return fetchApi<{ ok: boolean }>(`${base}/conversations/${id}/suggestions/dismiss`, json('POST', { kind, value }));
}

/** Open conversations still carrying the old Closed / Resolved labels (plan 1.4.2). */
export interface InboxLegacyRow {
  id: string;
  subject: string | null;
  contact: string | null;
  last_message_at: string | null;
  snippet: string | null;
  last_from_us: boolean;
  topic: string | null;
  owes_reply: boolean | null;
}

export function listLegacyOpen() {
  return fetchApi<{ ids: string[]; rows: InboxLegacyRow[] }>(`${base}/legacy`);
}

export function listSenderRules() {
  return fetchApi<{ rules: InboxSenderRule[] }>(`${base}/rules`);
}

export function createSenderRule(input: InboxSenderRuleInput) {
  return fetchApi<{ rules: InboxSenderRule[] }>(`${base}/rules`, json('POST', input));
}

export function updateSenderRule(id: string, input: InboxSenderRuleInput) {
  return fetchApi<{ rules: InboxSenderRule[] }>(`${base}/rules/${id}`, json('PATCH', input));
}

export function deleteSenderRule(id: string) {
  return fetchApi<{ rules: InboxSenderRule[] }>(`${base}/rules/${id}`, json('DELETE'));
}
